import React, { useMemo } from 'react';
import { Text, Group } from 'react-konva';

import FlashbackLayout from 'common/components/RewindModal/Konvas/FlashbackLayout';
import KonvaImage from 'common/components/RewindModal/Konvas/KonvaImage';
import ShareableStage from 'common/components/RewindModal/Konvas/ShareableStage';
import {
  LAYER_WIDTH,
  BLOCK_MARGIN,
  FLASHBACK_COVER_LOGO_WIDTH,
  FLASHBACK_COVER_LOGO_HEIGHT,
  FLASHBACK_COVER_LOGO_TOP_MARGIN,
  FLASHBACK_COVER_LOGO_OPACITY,
  ALLOCINE_LOGO_WIDTH,
  ALLOCINE_LOGO_HEIGHT,
  ALLOCINE_LOGO_BOTTOM_MARGIN,
  FLASHBACK_BACKGROUND_COLOR,
  FLASHBACK_TEXT_COLOR,
  FLASHBACK_GOLDEN_GRADIENT,
  FLASHBACK_FONT_FAMILY,
  FLASHBACK_VIEWING_TIME_HOURS_FONT_SIZE,
  FLASHBACK_VIEWING_TIME_HOURS_FONT_WEIGHT,
  FLASHBACK_VIEWING_TIME_HOURS_LINE_HEIGHT,
  FLASHBACK_VIEWING_TIME_TEXT_FONT_SIZE,
  FLASHBACK_VIEWING_TIME_TEXT_FONT_WEIGHT,
  FLASHBACK_VIEWING_TIME_TEXT_LINE_HEIGHT,
  FLASHBACK_VIEWING_TIME_PHRASE_FONT_SIZE,
  FLASHBACK_VIEWING_TIME_PHRASE_FONT_WEIGHT,
  FLASHBACK_VIEWING_TIME_PHRASE_LINE_HEIGHT,
  FLASHBACK_VIEWING_TIME_POLAROID_WIDTH,
  FLASHBACK_VIEWING_TIME_POLAROID_ROTATION
} from 'common/components/RewindModal/Konvas/constants';
import { getDPRSize } from 'common/components/RewindModal/helper';

type ScreenViewingTimeProps = {
  data: {
    total_runtime_hours: number;
    total_runtime_hours_phrase: string;
    image_name?: string;
  };
};

const ShareableScreenViewingTime = React.forwardRef<
  any,
  ScreenViewingTimeProps
>(({ data }, ref) => {
  const hours = data?.total_runtime_hours ?? 0;
  const phrase = data?.total_runtime_hours_phrase ?? '';

  const imagePath = useMemo(() => {
    if (!data?.image_name) return undefined;
    return `/commons/v9/allocine/flashback/${data.image_name}.png`;
  }, [data?.image_name]);

  const textLayout = useMemo(() => {
    const spacing_hours_to_text = BLOCK_MARGIN * 0.25;
    const spacing_text_to_phrase = BLOCK_MARGIN * 1.5;
    const spacing_phrase_to_image = BLOCK_MARGIN + getDPRSize(80);

    const hoursHeight =
      FLASHBACK_VIEWING_TIME_HOURS_FONT_SIZE *
      FLASHBACK_VIEWING_TIME_HOURS_LINE_HEIGHT;
    const textHeight =
      FLASHBACK_VIEWING_TIME_TEXT_FONT_SIZE *
      FLASHBACK_VIEWING_TIME_TEXT_LINE_HEIGHT;
    const phraseHeight =
      FLASHBACK_VIEWING_TIME_PHRASE_FONT_SIZE *
      FLASHBACK_VIEWING_TIME_PHRASE_LINE_HEIGHT;

    const y_hours = 0;
    const y_text = y_hours + hoursHeight + spacing_hours_to_text;
    const y_phrase = y_text + textHeight + spacing_text_to_phrase;
    const y_image = y_phrase + phraseHeight + spacing_phrase_to_image;

    return { y_hours, y_text, y_phrase, y_image };
  }, []);

  const layout = useMemo(() => {
    const contentHeight =
      textLayout.y_image + FLASHBACK_VIEWING_TIME_POLAROID_WIDTH * 1.2;

    const topMargin =
      FLASHBACK_COVER_LOGO_TOP_MARGIN +
      FLASHBACK_COVER_LOGO_HEIGHT +
      BLOCK_MARGIN * 2 +
      getDPRSize(30);
    const centerContentY = topMargin;

    return { centerContentY, contentHeight };
  }, [textLayout]);

  const centerX = LAYER_WIDTH / 2;

  return (
    <ShareableStage ref={ref}>
      <FlashbackLayout
        backgroundColor={FLASHBACK_BACKGROUND_COLOR}
        backgroundOpacity={1}
        coverLogoWidth={FLASHBACK_COVER_LOGO_WIDTH}
        coverLogoHeight={FLASHBACK_COVER_LOGO_HEIGHT}
        coverLogoTopMargin={FLASHBACK_COVER_LOGO_TOP_MARGIN}
        coverLogoOpacity={FLASHBACK_COVER_LOGO_OPACITY}
        allocineLogoWidth={ALLOCINE_LOGO_WIDTH}
        allocineLogoHeight={ALLOCINE_LOGO_HEIGHT}
        allocineLogoBottomMargin={ALLOCINE_LOGO_BOTTOM_MARGIN}
      >
        <Group y={layout.centerContentY}>
          <Group>
            <Text
              text={`${hours}h`}
              fontSize={FLASHBACK_VIEWING_TIME_HOURS_FONT_SIZE}
              fontFamily={FLASHBACK_FONT_FAMILY}
              fontStyle={FLASHBACK_VIEWING_TIME_HOURS_FONT_WEIGHT}
              lineHeight={FLASHBACK_VIEWING_TIME_HOURS_LINE_HEIGHT}
              width={LAYER_WIDTH}
              align="center"
              y={textLayout.y_hours}
              fillLinearGradientColorStops={FLASHBACK_GOLDEN_GRADIENT}
              fillLinearGradientStartPoint={{ x: 0, y: 0 }}
              fillLinearGradientEndPoint={{ x: LAYER_WIDTH, y: 0 }}
            />
            <Text
              text="de visionnage"
              fontSize={FLASHBACK_VIEWING_TIME_TEXT_FONT_SIZE}
              fontFamily={FLASHBACK_FONT_FAMILY}
              fontStyle={FLASHBACK_VIEWING_TIME_TEXT_FONT_WEIGHT}
              lineHeight={FLASHBACK_VIEWING_TIME_TEXT_LINE_HEIGHT}
              width={LAYER_WIDTH}
              align="center"
              y={textLayout.y_text}
              fillLinearGradientColorStops={FLASHBACK_GOLDEN_GRADIENT}
              fillLinearGradientStartPoint={{ x: 0, y: 0 }}
              fillLinearGradientEndPoint={{ x: LAYER_WIDTH, y: 0 }}
            />
          </Group>

          {phrase && (
            <Text
              text={phrase}
              fontSize={FLASHBACK_VIEWING_TIME_PHRASE_FONT_SIZE}
              fontFamily={FLASHBACK_FONT_FAMILY}
              fontStyle={FLASHBACK_VIEWING_TIME_PHRASE_FONT_WEIGHT}
              lineHeight={FLASHBACK_VIEWING_TIME_PHRASE_LINE_HEIGHT}
              fill={FLASHBACK_TEXT_COLOR}
              width={LAYER_WIDTH - BLOCK_MARGIN * 4}
              x={BLOCK_MARGIN * 2}
              align="center"
              y={textLayout.y_phrase}
            />
          )}

          {imagePath && (
            <Group
              x={centerX}
              y={textLayout.y_image}
              rotation={FLASHBACK_VIEWING_TIME_POLAROID_ROTATION}
              offsetX={FLASHBACK_VIEWING_TIME_POLAROID_WIDTH / 2}
              offsetY={0}
            >
              <KonvaImage
                path={imagePath}
                width={FLASHBACK_VIEWING_TIME_POLAROID_WIDTH}
                height={FLASHBACK_VIEWING_TIME_POLAROID_WIDTH * 1.2}
              />
            </Group>
          )}
        </Group>
      </FlashbackLayout>
    </ShareableStage>
  );
});

ShareableScreenViewingTime.displayName = 'ShareableScreenViewingTime';
export default ShareableScreenViewingTime;
